import { config } from '../../../stateManagement/redux/configVar';
import { Activity, CardAssociation } from './interfaces';
import { FormattedCardUploadData } from './utils';

const serverUrl = config.serverUrl;

export const getActivitiesToassociatedDynCards = async (
  startDate: string,
  endDate: string
): Promise<Activity[]> => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${serverUrl}/api/operations/getActivitiesToassociatedDynCards`,
    {
      method: 'GET',
      headers: {
        'Authorization': token,
        'startDate': startDate,
        'endDate': endDate,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 401) {
    localStorage.removeItem('authToken');
    throw new Error('Token expired. Please login again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Error: ${response.status}`);
  }

  const data = await response.json();
  return data;
};

export const associateCardToactivies = async (
  uploadData: FormattedCardUploadData[]
): Promise<{ success: boolean; processed: number; total: number; errors?: string[] }> => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  if (!uploadData || uploadData.length === 0) {
    throw new Error('No hay datos para subir');
  }

  const response = await fetch(
    `${serverUrl}/api/operations/uploadDynCardDataToFieldActivity`,
    {
      method: 'PATCH',
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(uploadData),
    }
  );

  if (response.status === 401) {
    localStorage.removeItem('authToken');
    throw new Error('Token expired. Please login again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || `Error: ${response.status}`;
    const errorDetails = errorData.errors ? ` Errores: ${errorData.errors.join(', ')}` : '';
    throw new Error(errorMessage + errorDetails);
  }

  const result = await response.json();

  // Verificar respuesta según documentación
  if (result.success === false && result.errors && result.errors.length > 0) {
    const errorMessage = `Procesados ${result.processed} de ${result.total} actividades. Errores: ${result.errors.join(', ')}`;
    throw new Error(errorMessage);
  }

  return {
    success: result.success !== false,
    processed: result.processed || 0,
    total: result.total || uploadData.length,
    errors: result.errors
  };
};

export const getSignedUrlForAttachment = async (
  fileName: string,
  fileType: string
): Promise<string> => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${serverUrl}/api/shared/getUrlToUploadFile`,
    {
      method: 'GET',
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json',
        'fileName': fileName,
        'fileType': fileType,
      },
    }
  );

  if (response.status === 401) {
    localStorage.removeItem('authToken');
    throw new Error('Token expired. Please login again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Error: ${response.status}`);
  }

  const data = await response.json();
  return data.url || data.signedUrl || '';
};

export const uploadFileToS3 = async (
  signedUrl: string,
  file: File
): Promise<void> => {
  const response = await fetch(signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file to S3: ${response.status}`);
  }
};

/**
 * Genera el fileKey para archivos adjuntos según el formato requerido
 * Formato: field_attached/fecha_inicio/group_assigned/filename
 */
export const generateAttachmentFileKey = (
  fileName: string,
  startDate: string,
  groupAssigned: string
): string => {
  // Normalizar el nombre del archivo (eliminar caracteres especiales y acentos)
  const normalizedFileName = fileName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_');

  // Formato: field_attached/fecha_inicio/group_assigned/filename
  const fileKey = `field_attached/${startDate}/${groupAssigned}/${normalizedFileName}`;
  return fileKey;
};

/**
 * Obtiene la signed URL para un archivo adjunto usando el fileKey
 */
export const getSignedUrlForAttachmentWithKey = async (
  fileKey: string,
  fileType: string
): Promise<string> => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${serverUrl}/api/shared/getUrlToUploadFile`,
    {
      method: 'GET',
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json',
        'fileName': fileKey,
        'fileType': fileType,
      },
    }
  );

  if (response.status === 401) {
    localStorage.removeItem('authToken');
    throw new Error('Token expired. Please login again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Error: ${response.status}`);
  }

  const data = await response.json();
  return data.url || data.signedUrl || '';
};

/**
 * Asocia un archivo subido a múltiples actividades
 */
export const associateFileToActivities = async (
  fileKey: string,
  fileName: string,
  fileType: string,
  activities: Activity[],
  startDate: string
): Promise<{ success: boolean; associated: number; errors?: string[] }> => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  if (!activities || activities.length === 0) {
    throw new Error('No hay actividades para asociar el archivo');
  }

  const errors: string[] = [];
  let successCount = 0;

  // Asociar el archivo a cada actividad
  for (const activity of activities) {
    try {
      const body = {
        name: fileName,
        fileKey: fileKey,
        contentType: fileType.replace('application/', '').split(';')[0] || 'octet-stream',
        locationName: activity.locationName || '',
        locationId: activity.locationId || '',
        fieldActivityId: activity.id,
        attachedRelatedDate: startDate,
        description: `Archivo adjunto asociado desde carga de cartas dinámicas`,
        lat: 0,
        lng: 0,
        tags: [],
        fileManagerType: "TAM"
      };

      const response = await fetch(
        `${serverUrl}/api/operations/asociateFieldActivityToOptimizationActivityByActivityId`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify(body),
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        throw new Error('Token expired. Please login again.');
      }

      // Parsear la respuesta
      const responseData = await response.json().catch(() => ({}));

      // Verificar si hay errores en la respuesta (aunque el status sea 200)
      if (!response.ok) {
        // Error de HTTP (4xx, 5xx)
        const errorMessage = responseData.message || `Error ${response.status}`;
        const errorDetails = responseData.errors && responseData.errors.length > 0
          ? responseData.errors.join(', ')
          : errorMessage;
        errors.push(`Actividad ${activity.locationName} (${activity.id}): ${errorDetails}`);
      } else if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
        // Respuesta exitosa pero con errores en el body
        const errorDetails = responseData.errors.join(', ');
        errors.push(`Actividad ${activity.locationName} (${activity.id}): ${errorDetails}`);
      } else if (responseData.message && responseData.message.toLowerCase().includes('no hay actividad')) {
        // Respuesta con mensaje de error sobre actividad no encontrada
        errors.push(`Actividad ${activity.locationName} (${activity.id}): ${responseData.message}`);
      } else if (responseData.processRecords === 0) {
        // Si processRecords es 0, puede indicar que no se procesó nada
        const errorMessage = responseData.message || 'No se pudo asociar el archivo a la actividad';
        errors.push(`Actividad ${activity.locationName} (${activity.id}): ${errorMessage}`);
      } else {
        // Éxito - se asoció correctamente
        successCount++;
      }
    } catch (error: any) {
      errors.push(`Actividad ${activity.locationName}: ${error.message}`);
    }
  }

  return {
    success: errors.length === 0,
    associated: successCount,
    errors: errors.length > 0 ? errors : undefined,
  };
};

